import { useEffect, useMemo, useState } from "react";
import TableContainer from "Common/TableContainer";
import BreadCrumb from "Common/BreadCrumb";
// import rolesData from "../../../assets/userRoles/rolesData.json";
import { useSelector } from "react-redux";
import { getRolesData } from "slices/thunk";
import { useDispatch } from "react-redux";

// Define types for Extensions and FlattenedRow
interface SubItem {
  name: string;
  role: {};
}

interface Extension {
  id: string;
  role: {};
  subItems: SubItem[];
}

interface FlattenedRow {
  id: string;
  subName: string | null;
  [key: string]: any;
}

const RBACTable = () => {
  // Store only the extensions dynamically based on roles data
  const dispatch = useDispatch<any>();
  const { extensions } = useSelector((state: any) => state.RBAC);
  const [roleExtensions, setRoleExtensions] = useState<Extension[]>(extensions);

  const roleNames = Array.from(
    new Set(roleExtensions.flatMap((ext) => Object.keys(ext.role || {})))
  );

  const flattenedData: FlattenedRow[] = [];

  roleExtensions.forEach((ext: any) => {
    // Main row for extension
    const mainRow: FlattenedRow = {
      id: ext.id,
      subName: null,
      ...roleNames.reduce((acc, role) => {
        acc[`${role}Enabled`] = ext.role?.[role] ?? false;
        return acc;
      }, {} as Record<string, boolean>),
    };
    flattenedData.push(mainRow);

    // Sub-rows for subItems
    ext.subItems.forEach((sub: any) => {
      flattenedData.push({
        id: ext.id,
        subName: sub.name,
        ...roleNames.reduce((acc, role) => {
          acc[`${role}Enabled`] = sub.role?.[role] ?? false;
          return acc;
        }, {} as Record<string, boolean>),
      });
    });
  });

  const toggleItem = (id: string, subName: string | null, role: string) => {
    setRoleExtensions((prev) =>
      prev.map((ext: any) => {
        if (ext.id !== id) return ext;

        if (!subName) {
          return {
            ...ext,
            role: {
              ...ext.role,
              [role]: !ext.role?.[role],
            },
          };
        } else {
          return {
            ...ext,
            subItems: ext.subItems.map((sub: any) =>
              sub.name === subName
                ? {
                    ...sub,
                    role: {
                      ...sub.role,
                      [role]: !sub?.role?.[role],
                    },
                  }
                : sub
            ),
          };
        }
      })
    );
  };

  const renderSwitch = (
    checked: boolean,
    onChange: () => void,
    id: string,
    subName: string | null,
    role: string
  ) => (
    <div className="flex justify-center cursor-pointer">
      <div className="relative inline-block w-8 align-middle transition duration-200 ease-in">
        <input
          type="checkbox"
          id={`${role}-switch-${id}-${subName || "main"}`}
          className="peer absolute block size-4 transition duration-300 ease-linear border-2 rounded-full appearance-none cursor-pointer border-slate-200 dark:border-zink-500 bg-white/80 dark:bg-zink-400 checked:bg-white dark:checked:bg-white ltr:checked:right-0 rtl:checked:left-0 checked:bg-none checked:border-custom-500 dark:checked:border-custom-500"
          checked={checked}
          onChange={onChange}
        />
        <label
          htmlFor={`${role}-switch-${id}-${subName || "main"}`}
          className="block h-4 overflow-hidden transition duration-300 ease-linear border rounded-full cursor-pointer border-slate-200 dark:border-zink-500 bg-slate-200 dark:bg-zink-600 peer-checked:bg-custom-500 peer-checked:border-custom-500"
        ></label>
      </div>
    </div>
  );

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: "Extension",
        accessorKey: "id",
        enableColumnFilter: false,
        cell: ({ row }: any) => (
          <span
            className={
              row.original.subName ? "text-transparent" : "font-medium"
            }
          >
            {row.original.subName ? "-" : row.original.id}
          </span>
        ),
      },
      {
        header: "Sub Extension",
        accessorKey: "subName",
        enableColumnFilter: false,
        cell: ({ row }: any) => <span>{row.original.subName}</span>,
      },
    ];

    const dynamicRoleColumns = roleNames.map((role) => ({
      header: role.charAt(0).toUpperCase() + role.slice(1), // Capitalize
      accessorKey: `${role}Enabled`,
      enableColumnFilter: false,
      cell: ({ row }: any) =>
        renderSwitch(
          row.original[`${role}Enabled`],
          () => toggleItem(row.original.id, row.original.subName, role),
          row.original.id,
          row.original.subName,
          role
        ),
    }));

    return [...baseColumns, ...dynamicRoleColumns];
  }, [roleNames, flattenedData]);

  useEffect(() => {
    dispatch(getRolesData());
  }, []);

  return (
    <div>
      <BreadCrumb title="RBAC Table" pageTitle="RBAC" />
      <TableContainer
        isPagination={false}
        columns={columns}
        data={flattenedData}
        customPageSize={50}
        divclassName="mt-5 overflow-x-auto"
        tableclassName="w-full whitespace-nowrap"
        theadclassName="text-center bg-slate-100 dark:bg-zink-600"
        thclassName="px-3.5 py-2.5 text-center font-semibold text-slate-500 border-b border-slate-200 dark:border-zink-500 dark:text-zink-200"
        tdclassName="px-3.5 py-2.5 border-y text-center border-slate-200 dark:border-zink-500"
        PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
      />
    </div>
  );
};

export default RBACTable;
